import React, { useState, useEffect } from 'react';
import { LuLogOut } from 'react-icons/lu';
import { MdViewAgenda, MdTableRows } from 'react-icons/md';
import apiClient from '../api/apiClient';

const statusColors = {
  completed: "bg-green-100 text-green-700",
  active: "bg-blue-100 text-blue-700",
  failed: "bg-red-100 text-red-700",
};

const TradesPage = () => {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('card');
  const [activeFilter, setActiveFilter] = useState('All');

  useEffect(() => {
    const fetchTrades = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiClient.get('/trades');
        // Map backend data to frontend structure
        const mappedTrades = response.data.map(trade => ({
          id: trade.id,
          name: trade.name,
          broker: trade.broker?.name || '',
          status: trade.status,
          entry: trade.entry_price,
          exit: trade.exit_price,
          pnl: trade.pnl,
          isProfit: trade.is_profit,
        }));
        setTrades(mappedTrades);
      } catch (error) {
        console.error('Error fetching trades:', error);
        setError('Failed to load trades. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchTrades();
  }, []);

  const filteredTrades = activeFilter === 'All' ? trades : trades.filter(t => t.status === activeFilter.toLowerCase());

  const handleExitTrade = (tradeId) => {
    console.log(`Exiting trade with id ${tradeId}`);
    // Implement exit trade logic here
  };

  return (
    <div className="bg-slate-50 min-h-screen p-3 sm:p-4 md:p-6 space-y-6">
      <h1 className="text-xl sm:text-2xl font-bold text-gray-900">📊 My Trades</h1>

      {/* View Toggle */}
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => setViewMode("card")}
          className={`p-2 rounded-md ${viewMode === "card" ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
        >
          <MdViewAgenda size={18} />
        </button>
        <button
          onClick={() => setViewMode("list")}
          className={`p-2 rounded-md ${viewMode === "list" ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
        >
          <MdTableRows size={18} />
        </button>
      </div>

      {/* Filters */}
      <div className="flex space-x-2 mb-4 overflow-x-auto">
        {['All', 'Active', 'Completed', 'Failed'].map(filter => (
          <button
            key={filter}
            onClick={() => setActiveFilter(filter)}
            className={`px-3 py-1.5 text-xs sm:text-sm rounded-full font-medium transition-colors ${
              activeFilter === filter
                ? "bg-blue-600 text-white shadow-sm"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            {filter} (
            {filter === "All"
              ? trades.length
              : trades.filter(t => t.status === filter.toLowerCase()).length}
            )
          </button>
        ))}
      </div>

      {/* Trades */}
      {loading && <p className="text-center text-gray-600">Loading trades...</p>}
      {error && <p className="text-center text-red-600">{error}</p>}

      {!loading && !error && (
        <div>
          {filteredTrades.length > 0 ? (
            viewMode === "card" ? (
              filteredTrades.map(trade => (
                <div key={trade.id} className="bg-white p-3 rounded-lg shadow-sm mb-3 border border-gray-100 hover:shadow transition-shadow">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{trade.name}</p>
                      <p className="text-xs text-gray-500">{trade.broker}</p>
                    </div>
                    <span className={`px-2 py-0.5 text-[11px] font-medium rounded-full ${statusColors[trade.status]}`}>
                      {trade.status}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-2 text-xs sm:text-sm">
                    <div>
                      <p className="text-gray-500">Entry: <span className="font-medium text-gray-800">{trade.entry}</span></p>
                      <p className="text-gray-500">Exit: <span className="font-medium text-gray-800">{trade.exit || "—"}</span></p>
                    </div>
                    <p className={`font-bold text-sm sm:text-base ${trade.isProfit ? "text-green-600" : "text-red-600"}`}>
                      ₹{trade.pnl.toLocaleString("en-IN")}
                    </p>
                  </div>
                  {trade.status === "active" && (
                    <div className="mt-2 pt-2 border-t border-gray-100">
                      <button
                        onClick={() => handleExitTrade(trade.id)}
                        className="w-full flex items-center justify-center gap-1 py-1.5 text-xs sm:text-sm bg-red-50 text-red-600 font-medium rounded-md hover:bg-red-100 transition-colors"
                      >
                        <LuLogOut size={14} />
                        Exit
                      </button>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="rounded-lg border border-gray-200 overflow-hidden">
                <div className="grid grid-cols-5 bg-gray-50 text-xs sm:text-sm font-semibold text-gray-600 px-3 py-2">
                  <div>Name</div>
                  <div>Status</div>
                  <div>Entry</div>
                  <div>Exit</div>
                  <div className="text-right">P&L</div>
                </div>
                {filteredTrades.map(trade => (
                  <div key={trade.id} className="grid grid-cols-5 items-center text-xs sm:text-sm py-2 px-3 border-b border-gray-100 bg-white hover:bg-gray-50 transition">
                    <div className="font-medium text-gray-800 truncate">{trade.name}</div>
                    <div>
                      <span className={`px-2 py-0.5 text-[11px] rounded-full ${statusColors[trade.status]}`}>
                        {trade.status}
                      </span>
                    </div>
                    <div className="text-gray-700">{trade.entry}</div>
                    <div className="text-gray-700">{trade.exit || "—"}</div>
                    <div className={`font-bold text-right ${trade.isProfit ? "text-green-600" : "text-red-600"}`}>
                      ₹{trade.pnl.toLocaleString("en-IN")}
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <p className="text-center text-gray-500 py-6 text-sm">No trades found for this filter.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default TradesPage;
